import { gql, useMutation, useQuery } from '@apollo/client';
import React from 'react';

import { DeleteAccountConfirmationModal } from 'src/components/common/deleteAccountConfirmationModal/DeleteAccountConfirmationModal';
import { Loading } from 'src/components/common/loading/Loading';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import {
  UI__DeleteUserMutation,
  UI__DeleteUserMutationVariables,
  UI__IsUserProbablyBillingContactQuery,
} from 'src/lib/graphqlTypes/types';

import { DeleteUserBlockerModal } from '../deleteUserBlockerModal/DeleteUserBlockerModal';

import {
  isUserPossiblyBillingContactForOrg,
  isUserProbablyBillingContactQuery,
} from './isUserPossiblyBillingContactForOrg';

const deleteUserMutation = gql<
  UI__DeleteUserMutation,
  UI__DeleteUserMutationVariables
>`
  mutation UI__DeleteUserMutation($userId: ID!) {
    user(id: $userId) {
      hardDelete
    }
  }
`;

export const DeleteUserModal = ({
  isOpen,
  userId,
  onCancel,
  onComplete,
}: {
  isOpen: boolean;
  userId: string;
  onCancel: () => void;
  onComplete: () => void;
}) => {
  const { data, loading } = useQuery<UI__IsUserProbablyBillingContactQuery>(
    isUserProbablyBillingContactQuery,
    {
      variables: {
        userId,
      },
      errorPolicy: 'all',
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );

  const [deleteUser, { loading: isSubmitting }] = useMutation<
    UI__DeleteUserMutation,
    UI__DeleteUserMutationVariables
  >(deleteUserMutation);

  const user = data?.user;

  if (loading || !user) {
    return <Loading />;
  }

  const orgsPreventingUserFromDeletingSelf = user.memberships
    .filter((membership) =>
      isUserPossiblyBillingContactForOrg(user, membership.account),
    )
    .map((membership) => ({
      id: membership.account.id,
      name: membership.account.name,
    }));

  const canUserDeleteSelf = orgsPreventingUserFromDeletingSelf.length === 0;

  if (canUserDeleteSelf) {
    return (
      <DeleteAccountConfirmationModal
        isOpen={isOpen}
        target="user account"
        isSubmitting={isSubmitting}
        onSubmitForDeletion={async () => {
          await deleteUser({ variables: { userId } });
          onComplete();
        }}
        onCancel={onCancel}
      >
        Deleting your user account will remove you from any organizations you’re
        a member of and will delete all your user data from Studio's systems.
      </DeleteAccountConfirmationModal>
    );
  }

  return (
    <DeleteUserBlockerModal
      isOpen={isOpen}
      onRequestClose={onCancel}
      orgsPreventingUserFromDeletingSelf={orgsPreventingUserFromDeletingSelf}
    />
  );
};
